import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const safeGetUser = () => {
  try {
    const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState(safeGetUser);
  const location = useLocation();

  React.useEffect(() => {
    setUser(safeGetUser());
  }, [location]);

  React.useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('nav-menu-open');
    } else {
      document.body.classList.remove('nav-menu-open');
    }
    return () => {
      document.body.classList.remove('nav-menu-open');
    };
  }, [isMenuOpen]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('authToken');
    setUser(null);
    window.location.href = '/signin';
  };

  const navItems = user
    ? [{ name: 'Dashboard', path: '/dashboard' }]
    : [
        { name: 'Home', path: '/' },
        { name: 'Team', path: '/team' },
        { name: 'Sponsors', path: '/sponsors' },
        { name: 'Gallery', path: '/gallery' },
        { name: 'Contests', path: '/contests' },
      ];

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <>
      <nav className="fixed top-0 w-full z-50 glass-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 min-h-[44px]">
            <Link to={user ? '/dashboard' : '/'} className="flex items-center space-x-3 group">
              <img
                src="/logo-icon.png"
                alt="Team Vortex Logo"
                className="h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
              />
              <span className="text-xl font-display font-bold gradient-text tracking-wider">
                TEAM VORTEX
              </span>
            </Link>

            <div className="hidden md:flex items-center space-x-8">
              {navItems.map(item => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`nav-link ${location.pathname === item.path ? 'text-vortex-blue' : ''}`}
                >
                  {item.name}
                </Link>
              ))}
              {user ? (
                <button
                  onClick={handleLogout}
                  className="glass-button text-red-400 border border-red-500/30 hover:bg-red-500/10 font-bold"
                >
                  LOGOUT
                </button>
              ) : (
                <Link
                  to="/signin"
                  className="glass-button text-vortex-blue border border-vortex-blue/30 hover:bg-vortex-blue hover:text-black font-bold"
                >
                  SIGN IN
                </Link>
              )}
            </div>

            <button
              onClick={toggleMenu}
              className="md:hidden p-2 text-white focus:outline-none z-50 active:scale-95 transition-transform min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <div className="w-6 h-[18px] relative">
                <span
                  className={`absolute left-0 right-0 h-[3px] bg-white rounded-full transition-all duration-300 ease-in-out ${
                    isMenuOpen ? 'top-[7.5px] rotate-45' : 'top-0'
                  }`}
                />
                <span
                  className={`absolute left-0 right-0 h-[3px] bg-white rounded-full transition-all duration-300 ease-in-out ${
                    isMenuOpen ? 'opacity-0 scale-0' : 'top-[7.5px]'
                  }`}
                />
                <span
                  className={`absolute left-0 right-0 h-[3px] bg-white rounded-full transition-all duration-300 ease-in-out ${
                    isMenuOpen ? 'top-[7.5px] -rotate-45' : 'top-[15px]'
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 md:hidden"
          >
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={toggleMenu} />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed right-0 top-0 h-full w-[min(20rem,85vw)] glass-nav border-l border-white/20 p-4 sm:p-6"
              style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top))' }}
            >
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-2">
                    <img
                      src="/logo-icon.png"
                      alt="Team Vortex Logo"
                      className="h-8 w-auto object-contain"
                    />
                    <span className="text-sm font-display font-bold text-white tracking-wider">
                      TEAM VORTEX
                    </span>
                    {user && (
                      <span className="text-white/60 font-medium text-xs ml-1">
                        | Hi, {user.name}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={toggleMenu}
                    className="p-2 text-white/80 hover:text-white transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <nav className="flex-1">
                  <ul className="space-y-4">
                    {navItems.map(item => (
                      <li key={item.name}>
                        <Link
                          to={item.path}
                          onClick={toggleMenu}
                          className={`block text-base font-medium py-3.5 px-5 rounded-xl transition-all duration-300 touch-manipulation ${
                            location.pathname === item.path
                              ? 'text-vortex-blue bg-vortex-blue/10 border border-vortex-blue/30'
                              : 'text-white/80 hover:text-vortex-blue hover:bg-white/5 glass-card'
                          }`}
                        >
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>

                {user ? (
                  <button
                    onClick={handleLogout}
                    className="w-full text-center glass-button text-red-400 border border-red-500/30 hover:bg-red-500/10 font-bold"
                  >
                    LOGOUT
                  </button>
                ) : (
                  <Link
                    to="/signin"
                    onClick={toggleMenu}
                    className="block text-center glass-button text-vortex-blue border border-vortex-blue/30 hover:bg-vortex-blue hover:text-black font-bold"
                  >
                    SIGN IN
                  </Link>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
